import { tracker } from '@lessonup/analytics';
import { useDocumentQuery, useLocale } from '@lessonup/client-integration';
import { logger } from '@lessonup/logging';
import {
  BreadcrumbList,
  ManagedModal,
  ModalHeaderV1,
  NiceModal,
  SpaceBetween,
  spacing,
  styled,
  toast,
  useErrorContext,
  useModal,
} from '@lessonup/ui-components';
import { AppError, assertNever } from '@lessonup/utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneratorInputType } from '../../../ChatGPTGenerator/MaiaGenerateLesson';
import { useConversionStatus } from '../../../SlideDeckImport/conversionStatus/useConversionStatus';
import { useUploadsStatus } from '../../../Uploads/hooks/useUploadsStatus';
import { useUploadStatusPolling } from '../../../Uploads/hooks/useUploadStatusPolling';
import { humandReadableFileSize } from '../../../Uploads/UploadsFeature.utils';
import { UploadRestrictionsDocument } from '../../Explorer.graphql.generated';
import { useUploadSlideDeckFile } from '../hooks/useUploadSlideDeckFile';
import { ChooseEditor } from './componentsV2/ChooseEditor';
import { ChooseNewEditorMode } from './componentsV2/ChooseNewEditorMode';
import { EditorSelection } from './componentsV2/EditorSelection';
import { GenerateLesson } from './componentsV2/GenerateLesson';
import { ImportLesson } from './componentsV2/ImportLesson';
import { TRANSLATION_NAMESPACE } from './constantsV2';
import { CreateLessonModalProps } from './model';

const MODAL_HEIGHT = '450px';
const MODAL_WIDTH = '600px';

type StepId = 'selectEditor' | 'classic' | 'new' | 'ai' | 'sdi' | 'lesson';

type Step = {
  id: StepId;
  label: string;
  parentId?: StepId;
};

export type EditorPreference = 'classic' | 'new' | 'generate' | 'import';

export type CreateLessonModalV2Response =
  | { type: 'lesson'; version: 'v1' | 'v2'; name: string }
  | { type: 'slideDeck'; name: string; lessonId: string; isWaitingOnConversion: boolean }
  | { type: 'maia'; generatorType: GeneratorInputType; version: 'v1' | 'v2' };

const CreateLessonModalV2 = NiceModal.create(({ lessonLocation, setEditorPreference }: CreateLessonModalProps) => {
  const modal = useModal();
  const { t } = useTranslation(TRANSLATION_NAMESPACE);
  const locale = useLocale();
  const [currentStep, setCurrentStep] = useState<StepId>('selectEditor');

  const { setError: setToastError } = useErrorContext();
  const [invalidFileError, setInvalidFileError] = useState<string | undefined>();
  const [uploadInProgressId, setUploadInProgressId] = useState<string | undefined>();
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const { startPollingForIds, stopPolling } = useUploadStatusPolling({
    onError: (error) => {
      setToastError({
        error,
        customErrorDisplayMessage: {
          title: t('errors.conversionFailedTitle'),
          description: t('errors.genericPolling'),
        },
      });
      logger.error(error, { context: 'CreateNewLessonModal slide deck conversion status polling' });
      setIsConverting(false);
    },
  });
  const { data: uploadRestrictions } = useDocumentQuery(UploadRestrictionsDocument, {
    fetchPolicy: 'cache-first',
  });

  const { setConversionStatus, removeConversionStatus } = useConversionStatus();

  const [performUpload] = useUploadSlideDeckFile({
    onError: (error) => setToastError({ error }),
  });

  useUploadsStatus({
    ids: uploadInProgressId ? [uploadInProgressId] : [],
    onAllCompleted: (data) => {
      setUploadInProgressId(undefined);
      setIsConverting(false);
      if (!data) throw new AppError('unexpected-data', 'Expected data about completed uploads', { uploadInProgressId });
      if (data.viewer.uploadsByIds.length !== 1)
        throw new AppError('unexpected-data', 'Expected exactly one upload to be completed', {
          uploadIds: data.viewer.uploadsByIds.map(({ id }) => id),
        });

      const upload = data.viewer.uploadsByIds[0];

      if (upload.__typename !== 'SlideDeckUpload') {
        throw new AppError('unexpected-data', 'Expected status of a SlideDeckUpload', { uploadId: upload.id });
      }

      tracker.events.lessonPowerpointConverted({
        lessonId: upload.lessonId || '',
        pinCount: upload.slideCount || 0,
        fileSizeInBytes: upload.originalFile.sizeInBytes.toString(),
      });

      modal.resolve({
        type: 'slideDeck',
        name: upload.name,
        isWaitingOnConversion: true,
        lessonId: upload.lessonId,
      });
      modal.hide();
    },
    onSingleDocumentError() {
      setToastError({
        error: null,
        customErrorDisplayMessage: {
          title: t('errors.conversionFailedTitle'),
          description: t('errors.singleConversionFailed'),
        },
      });
      unsetConversionStatus();
    },
  });

  const onCreateLessonFromSlideDeck = async (name: string, file: File) => {
    setIsConverting(true);
    assertSlideDeckValidity(file);
    const uploadId = await performUpload({
      file,
      lessonName: name,
      lessonLocation,
    });
    setUploadInProgressId(uploadId);

    if (!uploadId) {
      setIsConverting(false);
      return modal.reject(
        new AppError('unexpected-data', 'No upload ID, upload not created correctly', { name, file })
      );
    }

    startPollingForIds([uploadId]);

    setConversionStatus({ uploadId, status: 'UPLOADING', name, createdAt: new Date() });
    modal.resolve({ name, type: 'slideDeck', isWaitingOnConversion: true });
    modal.hide();
  };

  const createManualLesson = (name: string, version: 'v1' | 'v2') => {
    setEditorPreference(version === 'v1' ? 'classic' : 'new');
    modal.resolve({ name, type: 'lesson', version });
    modal.hide();
  };

  const assertSlideDeckValidity = (file: File) => {
    if (!uploadRestrictions) return;
    const tooBig = file.size > uploadRestrictions.viewer.maxFileSizeInBytesForUploads;

    if (tooBig) {
      setInvalidFileError(
        t('errors.fileSizeTooBig', {
          maxFileSizeInMB: humandReadableFileSize(uploadRestrictions.viewer.maxFileSizeInBytesForUploads, locale),
        })
      );
      return;
    }

    const fileTypeIsAllowed = uploadRestrictions?.viewer.acceptedMimeTypesForUploads.slideDeckTypes?.includes(
      file.type
    );

    if (!fileTypeIsAllowed) {
      setInvalidFileError(t('errors.fileTypeNotSupported'));
      return;
    }

    setInvalidFileError(undefined);
  };

  function onBeforeClose() {
    if (!isConverting) {
      return;
    }
    toast({ type: 'neutral', message: t('closeDuringConversion') });
    unsetConversionStatus();
  }

  function unsetConversionStatus() {
    if (uploadInProgressId) removeConversionStatus({ uploadId: uploadInProgressId });
    setIsConverting(false);
    stopPolling();
  }

  const steps: Step[] = [
    { id: 'selectEditor', label: t('selectEditorTitle') },
    { id: 'classic', label: t('classicEditorTitle'), parentId: 'selectEditor' },
    { id: 'new', label: t('newEditorTitle'), parentId: 'selectEditor' },
    { id: 'lesson', label: t('lessonTitle'), parentId: 'new' },
    { id: 'sdi', label: t('sdiTitle'), parentId: 'new' },
    { id: 'ai', label: t('aiTitle'), parentId: 'new' },
  ];

  function displayStep(step: StepId) {
    switch (step) {
      case 'selectEditor':
        return <ChooseEditor onSelect={(editor: 'classic' | 'new') => navigateToStep(editor)} />;
      case 'classic':
        return (
          <EditorSelection
            editorType="v1"
            createManualLesson={createManualLesson}
            back={() => navigateToStep('selectEditor')}
          />
        );
      case 'new':
        return (
          <ChooseNewEditorMode
            onSelect={(editor: 'ai' | 'lesson' | 'sdi') => navigateToStep(editor)}
            back={() => navigateToStep('selectEditor')}
          />
        );
      case 'lesson':
        return (
          <EditorSelection editorType="v2" createManualLesson={createManualLesson} back={() => navigateToStep('new')} />
        );
      case 'sdi':
        return (
          <ImportLesson
            modal={modal}
            onCreateLessonFromSlideDeck={onCreateLessonFromSlideDeck}
            invalidFileError={invalidFileError}
            loading={isConverting}
            onSelectFile={assertSlideDeckValidity}
            back={() => navigateToStep('new')}
          />
        );
      case 'ai':
        return (
          <GenerateLesson
            modal={modal}
            editorType="v2"
            hideUnderConstructionBanner={true}
            back={() => navigateToStep('new')}
          />
        );
      default:
        assertNever(step, 'Unknown step');
    }
  }

  function navigateToStep(step: StepId) {
    setCurrentStep(step);
  }

  function getBreadCrumbList(stepId: StepId, steps: Step[]): Step[] {
    const map = new Map(steps.map((step) => [step.id, step]));
    const initialStep = steps.find((x) => x.id === stepId);
    if (!initialStep) return [];

    const chain: Step[] = [initialStep];

    let current = initialStep;
    while (current?.parentId) {
      const parent = map.get(current.parentId);
      if (!parent) break;
      chain.unshift(parent);
      current = parent;
    }

    return chain;
  }

  function getBreadcrumbs() {
    return getBreadCrumbList(currentStep, steps).map((x) => ({ label: x.label, onClick: () => navigateToStep(x.id) }));
  }

  return (
    <ManagedModal
      modal={modal}
      contentLabel={t('title')}
      onBeforeClose={onBeforeClose}
      width={MODAL_WIDTH}
      height={MODAL_HEIGHT}
      maxHeight={MODAL_HEIGHT}
      overflowHidden
    >
      <SpaceBetween direction="y" spacing={spacing.size4} justifyContent="space-between">
        {currentStep !== 'selectEditor' && (
          <ModalHeaderV1
            type="breadcrumb"
            title={{ content: <BreadcrumbList title={t('title')} breadCrumbs={getBreadcrumbs()} /> }}
            onCloseButtonClick={() => modal.hide()}
            showDivider={false}
            overflow="wrap"
          />
        )}
        <StyledSpaceBetween direction="y" spacing={spacing.size24}>
          {displayStep(currentStep)}
        </StyledSpaceBetween>
      </SpaceBetween>
    </ManagedModal>
  );
});

export const showCreateLessonModalV2 = (props: CreateLessonModalProps): Promise<CreateLessonModalV2Response> => {
  return NiceModal.show(CreateLessonModalV2, props);
};

const StyledSpaceBetween = styled(SpaceBetween)`
  width: 100%;
  flex-grow: 1;
  height: ${MODAL_HEIGHT};
  min-height: calc(${MODAL_HEIGHT} - 60px);
  overflow-y: scroll;
  height: 80vh;
  flex: 1;
  overflow: auto;
  margin-left: 0 !important; // Override initial SpaceBetween margin
`;
